
import React, { createContext, useContext, useState, useEffect } from 'react';
import { PaymentInfo } from '@/hooks/useCourierPayments';
import { useCourierInfo } from '@/pages/courier/hooks/useCourierInfo';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';

interface AuthPaymentContextType {
  authPayment: PaymentInfo | null;
  isAuthPaymentLoading: boolean;
  refreshAuthPayment: () => Promise<void>;
}

const AuthPaymentContext = createContext<AuthPaymentContextType>({
  authPayment: null,
  isAuthPaymentLoading: false,
  refreshAuthPayment: async () => {},
});

export const useAuthPayment = () => useContext(AuthPaymentContext);

export const AuthPaymentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authPayment, setAuthPayment] = useState<PaymentInfo | null>(null);
  const [isAuthPaymentLoading, setIsAuthPaymentLoading] = useState<boolean>(true);
  const { courierId, loading: courierLoading } = useCourierInfo();
  
  const fetchAuthPayment = async (id: string) => {
    try {
      setIsAuthPaymentLoading(true);
      
      console.log('AuthPaymentContext: Проверка авторизационных платежей для курьера:', id);
      
      // Получаем последний платеж со статусом 'auth'
      const { data, error } = await supabase
        .from('payments')
        .select('*, rental:rentals(id, human_number)')
        .eq('courier_id', id)
        .eq('status', 'auth')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle(); // Используем maybeSingle вместо single()
      
      if (error && error.code !== 'PGRST116') {
        // PGRST116 - это код ошибки "Не найдено", игнорируем его
        console.error('AuthPaymentContext: Ошибка при получении авторизационного платежа:', error);
        // Не прерываем выполнение, продолжаем работу
        setAuthPayment(null);
        return;
      }
      
      if (data) {
        console.log('AuthPaymentContext: Найден авторизационный платеж:', data);
        
        // ИСПРАВЛЕНО: Проверяем и приводим payment_method к правильному типу
        const paymentMethod = data.payment_method || 'card';
        const validPaymentMethod = ['card', 'cash', 'account', 'sbp'].includes(paymentMethod) 
          ? paymentMethod as 'card' | 'cash' | 'account' | 'sbp'
          : 'card';
        
        // Создаем объект платежа с правильными полями и типами
        const paymentInfo: PaymentInfo = {
          id: data.id,
          courierId: data.courier_id,
          amount: data.amount || 0,
          date: data.payment_date || data.created_at,
          status: 'auth',
          method: validPaymentMethod,
          rentalId: data.rental_id,
          rentalHumanNumber: data.rental?.human_number,
          receiptUrl: data.receipt_url,
          human_number: data.human_number,
          subscriptionId: data.subscription_id,
          courierName: 'Курьер' // Добавляем обязательное поле courierName
        };
        
        setAuthPayment(paymentInfo);
      } else {
        console.log('AuthPaymentContext: Авторизационные платежи не найдены');
        setAuthPayment(null);
      }
    } catch (error) {
      console.error('AuthPaymentContext: Непредвиденная ошибка при получении авторизационного платежа:', error);
      // В случае ошибки устанавливаем null чтобы не блокировать пользователя
      setAuthPayment(null);
    } finally {
      console.log('AuthPaymentContext: Завершение загрузки авторизационных платежей');
      setIsAuthPaymentLoading(false);
    }
  };
  
  const refreshAuthPayment = async () => {
    if (courierId) {
      await fetchAuthPayment(courierId);
    }
  };
  
  // Загружаем данные при монтировании и изменении courierId
  useEffect(() => {
    console.log('AuthPaymentContext: useEffect вызван:', { courierId, courierLoading });
    
    if (courierId && !courierLoading) {
      refreshAuthPayment();
    } else if (!courierId && !courierLoading) {
      // Если нет courierId и загрузка завершена, сбрасываем состояние
      console.log('AuthPaymentContext: Нет courier ID, сбрасываем состояние');
      setAuthPayment(null);
      setIsAuthPaymentLoading(false);
    }
    
    // Принудительное завершение загрузки через 8 секунд
    const timeout = setTimeout(() => {
      if (isAuthPaymentLoading) {
        console.warn('AuthPaymentContext: Принудительное завершение загрузки через timeout');
        setIsAuthPaymentLoading(false);
      }
    }, 8000);
    
    return () => clearTimeout(timeout);
  }, [courierId, courierLoading]);
  
  const value = {
    authPayment,
    isAuthPaymentLoading,
    refreshAuthPayment
  };
  
  return (
    <AuthPaymentContext.Provider value={value}>
      {children}
    </AuthPaymentContext.Provider>
  );
};
